
import { mapActions, mapGetters, mapState } from 'vuex';
import { Options, Vue } from 'vue-class-component';
import { LogLevel, View } from '@/interfaces';
import DailyCo from '@/daily';
import { User } from '@/consts';
import { RecState } from '@/enums';
import store from '@/store';
import ExamIntro from './ExamIntro.vue';
import ExamQuestion from './ExamQuestion.vue';
import ExaminerVideo from './ExaminerVideo.vue';
import ProgressIndicator from './ProgressIndicator.vue';
import ModuleTypeIcon from './ModuleTypeIcon.vue';

@Options({
  components: {
    ExaminerVideo,
    ExamIntro,
    ExamQuestion,
    ProgressIndicator,
    ModuleTypeIcon,
  },
  inject: ['log'],
  sockets: {
    confirmStartRecording(isStartRecordingConfirmed: boolean) {
      if (isStartRecordingConfirmed) {
        this.next(this.log);
        this.$socket.emit('startRecording');
      }
    },
    confirmStopRecording(isStopRecordingConfirmed: boolean) {
      if (isStopRecordingConfirmed) {
        this.next(this.log);
        this.$socket.emit('stopRecording');
        this.log({
          message: 'Next step',
          context: { action: 'next-step' },
        });
      }
    },
    examNextStep() {
      const NEXT_STEP_REC_STATE = this.$store.getters['exam/nextStepRecState'];

      switch (true) {
        case this.view.recState === RecState.START_RECORDING && NEXT_STEP_REC_STATE === RecState.RECORDING: {
          this.$socket.emit('confirmStartRecording');
          return;
        }

        case this.view.recState === RecState.RECORDING && NEXT_STEP_REC_STATE === RecState.STOP_RECORDING: {
          this.$socket.emit('confirmStopRecording');
          return;
        }

        case this.view.recState === RecState.RECORDING: {
          this.next(this.log);
          this.log({
            message: 'Next step',
            context: { action: 'next-step' },
          });
          break;
        }

        default:
          break;
      }
    },
    examPreviousStep() {
      const PREVIOUS_STEP_REC_STATE = this.$store.getters['exam/previousStepRecState'];

      switch (true) {
        case this.view.recState === RecState.RECORDING && PREVIOUS_STEP_REC_STATE === RecState.START_RECORDING: {
          return;
        }

        case this.view.recState === RecState.STOP_RECORDING: {
          return;
        }

        case this.view.recState === RecState.RECORDING: {
          this.prev(this.log);
          this.log({
            message: 'Previous step',
            context: { action: 'previous-step' },
          });
          break;
        }

        default:
          break;
      }
    },
    examReplay() {
      this.replay(this.log);
      this.log({
        message: 'Replay audio',
        context: { action: 'replay-audio' },
      });
    },
    sessionUpdated(session: any) {
      this.updateSession(session);
      this.joinDailyCall();
    },
  },

  data() {
    return {
      isImageError: false,
      isJoined: false,
      networkInterval: null,
      isScreenSharing: false,
      screenShareAttempts: 0,
    };
  },

  computed: mapState({
    session: (state: any) => state.session.data,
    examiner: (state: any) => state.session.examiner,
    modules: (state: any) => state.exam.modules,
    examError: (state: any) => state.exam.error,
  }),
  watch: {
    isScreenSharing: {
      handler() {
        if (!this.isScreenSharing) {
          this.startScreenShare();
        }

        this.log({
          message: this.isScreenSharing ? 'Screen sharing started' : 'Screen sharing stopped',
          context: { action: 'screen-share', isScreenSharing: this.isScreenSharing },
        });
      },
    },
    networkInterval: {
      handler() {
        this.sendExaminerNetworkQuality();
      },
    },
  },
  mounted() {
    this.getExamQuestions(this.log);
    document.addEventListener('keyup', event => {
      switch (event.code) {
        case 'ArrowLeft':
          store.dispatch('prev', this.log);
          break;
        case 'ArrowRight':
          store.dispatch('next', this.log);
          break;
        case 'ArrowUp':
          store.dispatch('replay', this.log);
          break;
        default:
          break;
      }
    });
  },
  methods: {
    ...mapActions(['next', 'prev', 'replay', 'updateSession', 'updateExaminer', 'getExamQuestions']),
    ...mapGetters('exam', ['modules', 'nextStepRecState', 'previousStepRecState']),
    async sendExaminerNetworkQuality() {
      if (!this.networkInterval && this.isJoined) {
        this.networkInterval = setInterval(async () => {
          try {
            const res = await DailyCo.getDaily().getNetworkStats();
            this.$socket.emit(
              'connectionQuality',
              { user: User.EXAM, threshold: res?.threshold || null, quality: res?.quality || 0 },
            );
            clearInterval(this.networkInterval);
            this.networkInterval = null;
          } catch (error) {
            this.log({
              logLevel: LogLevel.ERROR,
              message: 'Failed to get network stats',
              context: {
                action: 'get-network-stats',
                error,
              },
            });
            clearInterval(this.networkInterval);
            this.networkInterval = null;
          }
        }, 2000);
      }
    },
    joinDailyCall() {
      if (this.session.daily.clientToken && this.session.daily.roomUrl && !DailyCo.getIsDailyCallCreated()) {
        DailyCo.createDailyCall();

        DailyCo.joinDailyCall({
          clientToken: this.session.daily.clientToken,
          roomUrl: this.session.daily.roomUrl,
          userName: 'exam-app',
        });

        DailyCo.getDaily()
          .on('joined-meeting', () => this.updateParticipants('joined-meeting'))
          .on('participant-joined', this.updateParticipants)
          .on('participant-updated', this.updateParticipants)
          .on('participant-left', this.updateParticipants)
          .on('left-meeting', this.leftMeeting)
          .on('local-screen-share-canceled', this.startScreenShare)
          .on('local-screen-share-stopped', this.startScreenShare);
      }
    },
    leftMeeting() {
      this.log({
        message: 'Left the meeting',
        context: { action: 'left-meeting' },
      });
    },
    updateParticipants(e: string) {
      if (!DailyCo.getIsDailyCallCreated()) return;

      if (e === 'joined-meeting') {
        this.isJoined = true;
        this.log({
          message: 'Joined the meeting',
          context: { action: 'joined-meeting' },
        });
        this.sendExaminerNetworkQuality();
        this.$socket.emit('isUserConnected', { name: User.EXAM, connected: true });

        this.startScreenShare();
      }

      if (e === 'left-meeting') {
        this.isJoined = false;
      }

      this.participants = Object.values(DailyCo.getDaily().participants());

      this.isScreenSharing = DailyCo.getDaily().participants().local.screen;

      this.examiner = Object.values(this.participants).reduce((participantsAcc, participant: any) => {
        if (participant.user_name === 'examiner') {
          participantsAcc = {
            userId: participant.user_id,
            audio: participant.audio,
            video: participant.video,
            videoTrack: participant.tracks.video,
          };
        }

        return participantsAcc;
      }, {});

      this.updateExaminer(
        Object.values(this.participants).reduce((participantsAcc, participant: any) => {
          if (participant.user_name === 'examiner') {
            participantsAcc = {
              userId: participant.user_id,
              audio: participant.audio,
              video: participant.video,
              videoTrack: participant.tracks.video,
            };
          }

          return participantsAcc;
        }, {}),
      );

      this.loading = false;
    },
    startScreenShare() {
      if (!DailyCo.getDaily()) {
        console.warn('[EXAM-APP] startScreenShare skipped: no Daily call');
        this.log({
          message: 'startScreenShare skipped: no Daily call',
          context: { action: 'screen-share-start' },
        });
        return;
      }

      this.screenShareAttempts += 1;
      const attempt = this.screenShareAttempts;

      console.log(`[EXAM-APP] startScreenShare attempt #${attempt}`);
      this.log({
        message: `startScreenShare attempt #${attempt}`,
        context: { action: 'screen-share-start', attempt },
      });

      try {
        const result: any = DailyCo.getDaily().startScreenShare({
          displayMediaOptions: {
            audio: true,
            video: true,
            selfBrowserSurface: 'include',
            systemAudio: 'include',
          },
        });

        if (result && typeof result.then === 'function') {
          result
            .then(() => {
              console.log(`[EXAM-APP] startScreenShare #${attempt} resolved`);
              this.log({
                message: `startScreenShare #${attempt} resolved`,
                context: { action: 'screen-share-resolved', attempt },
              });
            })
            .catch((err: any) => {
              console.error(
                `[EXAM-APP] startScreenShare #${attempt} rejected:`,
                err?.name,
                err?.message,
              );
              this.log({
                message: `startScreenShare #${attempt} rejected`,
                context: {
                  action: 'screen-share-rejected',
                  attempt,
                  error: { name: err?.name, message: err?.message, stack: err?.stack },
                },
              });
            });
        }
      } catch (err: any) {
        console.error(
          `[EXAM-APP] startScreenShare #${attempt} threw:`,
          err?.name,
          err?.message,
        );
        this.log({
          message: `startScreenShare #${attempt} threw`,
          context: {
            action: 'screen-share-threw',
            attempt,
            error: { name: err?.name, message: err?.message, stack: err?.stack },
          },
        });
      }
    },
  },
})
export default class App extends Vue {
  get brand(): string {
    return this.$store.state.brand;
  }

  get examId(): string {
    return this.$store.state.exam.id || '';
  }

  get loading(): boolean {
    return this.$store.state.loading;
  }

  get originUrl(): string {
    return this.$store.state.originUrl;
  }

  get view(): View {
    return this.$store.getters['exam/currentView'];
  }

  get moduleKey(): string {
    const title = this.view.title || '';
    const colonIndex = title.indexOf(':');
    return colonIndex > -1 ? title.substring(0, colonIndex).trim() : '';
  }

  get moduleTitle(): string {
    const title = this.view.title || '';
    const colonIndex = title.indexOf(':');
    return colonIndex > -1 ? title.substring(colonIndex + 1).trim() : title;
  }

  get isFullBleedImage(): boolean {
    return !!this.view.image && (this.moduleKey === '3A' || this.moduleKey === '3B');
  }
}
